import { Link, Navigate } from 'react-router-dom';
import { ClipboardCheck, Cylinder, Loader2 } from 'lucide-react';

import { useIsSuperadmin } from '@/hooks/admin/useIsSuperadmin';

/**
 * AuditHome — the landing page of the Gas Cylinder Rental Fees (/audit).
 *
 * Two cards, one per section:
 *   - Cylinder Audit → /audit/count (pick the customer you are standing at)
 *   - Rental Fees (Management) → /audit/rentals (enrollment, monthly
 *     billing, estimates, history) — superadmin only.
 *
 * A DRIVER with only audit ability never sees this page: they are sent
 * straight to the customer picker, because a menu with one usable entry is a
 * click for nothing. The redirect waits for the superadmin check to answer —
 * sending a superadmin to the picker on a not-yet-loaded flag would hide the
 * rentals card from the one person it is for.
 */
export default function AuditHome() {
  const { isSuperadmin, loading } = useIsSuperadmin();

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-24 text-brix-muted">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-sm">Loading…</span>
      </div>
    );
  }

  if (!isSuperadmin) return <Navigate to="/audit/count" replace />;

  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="font-display text-xl font-bold tracking-display-tight text-brix-ink">
        What are you here to do?
      </h1>
      <p className="mt-1 text-sm text-brix-muted">
        Count cylinders at a customer, or manage gas cylinder rental fees.
      </p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <Link
          to="/audit/count"
          data-testid="home-card-audit"
          className="group flex flex-col rounded-2xl border border-brix-border bg-brix-surface p-6 shadow-card transition-shadow hover:shadow-card-hover"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brix-primary/10 text-brix-primary">
            <ClipboardCheck className="h-6 w-6" />
          </div>
          <h2 className="mt-4 font-display text-lg font-bold text-brix-ink">Cylinder Audit</h2>
          <p className="mt-1 text-sm text-brix-muted">
            Pick the customer you are at, count the tanks on site, take photos, sign, and
            file the adjustment.
          </p>
          <span className="mt-4 text-sm font-semibold text-brix-primary group-hover:underline">
            Start an audit →
          </span>
        </Link>

        <Link
          to="/audit/rentals"
          data-testid="home-card-rentals"
          className="group flex flex-col rounded-2xl border border-brix-border bg-brix-surface p-6 shadow-card transition-shadow hover:shadow-card-hover"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brix-primary/10 text-brix-primary">
            <Cylinder className="h-6 w-6" />
          </div>
          <h2 className="mt-4 font-display text-lg font-bold text-brix-ink">
            Rental Fees
          </h2>
          <p className="mt-1 text-sm text-brix-muted">
            Gas cylinder rental fees for every gas we rent (CO₂, nitrogen blends and more): who is
            billed, this month's invoices, the estimate, and each account's cylinder history and audits.
          </p>
          <span className="mt-4 text-sm font-semibold text-brix-primary group-hover:underline">
            Open rental fees →
          </span>
        </Link>
      </div>

      <p className="mt-6 text-xs text-brix-muted">
        Staff only. Drivers with audit access land directly on the customer list.
      </p>
    </div>
  );
}
